import React, { useEffect, useState, useContext } from 'react';

import { Box, Button, Card, CardContent, Typography, Stack, FormHelperText, TextField, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import { Container } from '@mui/system';
import List from '@mui/material/List';
import { collection, query, onSnapshot, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from "yup";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { db } from '../../firebase';
import AdminNavbar from '../../components/AdminNavbar';
import { currencyFormatter } from '../../helpers';
import AlertsContext from '../../AlertsContext';
import { format } from 'date-fns';

const schema = yup.object({
  code: yup.string().required().label('Code'),
  amount: yup.number().typeError('Amount must be a number').required().label('Amount'),
  expiryDate: yup.date().required().label('Expiry Date'),
}).required();

function Vouchers() {
  const { setAlert } = useContext(AlertsContext);
  const [vouchers, setVouchers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, reset, formState: { errors, isDirty, isValid } } = useForm({
    defaultValues: {
      code: '',
      amount: '',
      expiryDate: new Date(),
    },
    resolver: yupResolver(schema)
  });

  const voucherCollection = collection(db, 'vouchers');

  const onSubmit = async data => {
    try {
      setIsLoading(true)
      const isExists = vouchers && vouchers.findIndex(voucher => voucher.code === data?.code) !== -1;
      if (isExists) {
        setAlert("Voucher code already exists.");

        return;
      }
      await addDoc(voucherCollection, {
        ...data,
        amountRemaining: data?.amount,
      })
      setAlert("Voucher is added successful.");
      reset();
    } catch (e) {
      console.log(e);
      setAlert("Error.");
    } finally {
      setIsLoading(false);
    }
  }

  const deleteVoucher = async (id) => {
    if (!window.confirm("Are you sure you want to delete this voucher?")) return;
    const vouchersRef = doc(db, 'vouchers', id);
    try {
      setIsLoading(true)
      await deleteDoc(vouchersRef);
      setAlert("Voucher is deleted successful.");
    } catch (e) {
      setAlert("Error.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onSnapshot(query(voucherCollection), (querySnapshot) => {
      const list = [];
      querySnapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id });
      });
      setVouchers(list);
    });
  }, []);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs} >
        <AdminNavbar />
        <Container component="main" maxWidth="xs" sx={{
          marginTop: '16px',
          marginBottom: '16px',
        }}>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                  <Controller
                    name="code"
                    control={control}
                    render={({ field }) => <TextField type="text" label="Code" variant="outlined" {...field} />}
                  />
                  {
                    errors?.code && (
                      <FormHelperText error>{errors.code?.message}</FormHelperText>
                    )
                  }
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field }) => <TextField type="text" label="Amount" variant="outlined" InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }} {...field} />}
                  />
                  {
                    errors?.amount && (
                      <FormHelperText error>{errors.amount?.message}</FormHelperText>
                    )
                  }
                  <Controller
                    name="expiryDate"
                    control={control}
                    render={({ field }) => <DesktopDatePicker
                      label="Expiry Date"
                      disablePast
                      inputFormat="DD/MM/YYYY"
                      value={field.value}
                      onChange={value => field.onChange(value)}
                      renderInput={(params) => <TextField {...field} {...params} />}
                    />}
                  />
                  {
                    errors?.expiryDate && (
                      <FormHelperText error>{errors.expiryDate?.message}</FormHelperText>
                    )
                  }
                  <LoadingButton
                    disabled={!isDirty && !isValid}
                    loading={isLoading}
                    sx={{ margin: '0 auto', marginTop: 2, alignItems: 'center' }}
                    type='submit'
                    variant='contained'
                  >
                    Generate a Voucher
                  </LoadingButton>
                </Stack>
              </form>
            </CardContent>
          </Card>
          <h2>Vouchers</h2>
          <List sx={{ width: '100%', margin: '0 auto', marginTop: 2, alignItems: 'center' }}>
            {
              vouchers && vouchers.length > 0 ? (
                <>
                  {vouchers.map((voucher) => {
                    return (
                      <Card key={voucher.id} sx={{
                        marginTop: 4,
                      }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant='h6' color="text.secondary">
                                Voucher
                              </Typography>
                              <Typography color="text.secondary">
                                {currencyFormatter(Number(voucher?.amount))}
                              </Typography>
                              <Typography color="text.secondary" sx={{
                                fontSize: '14px',
                              }}>
                                {currencyFormatter(Number(voucher?.amountRemaining))} Available
                              </Typography>
                              <Typography color="text.secondary">
                                Expiry {voucher?.expiryDate && format(voucher.expiryDate.toDate(), 'dd/MM/yyyy')}
                              </Typography>
                            </Box>
                            <Box>
                              <Stack direction="row" spacing={1}>
                                <Button sx={{
                                  height: '100%'
                                }} variant="outlined">{voucher?.code}</Button>
                                <IconButton onClick={() => deleteVoucher(voucher?.id)} sx={{
                                  height: '100%'
                                }} variant="outlined" color="error"><DeleteIcon /></IconButton>
                              </Stack>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              ) : <Box>
                <Typography>No vouchers.</Typography>
              </Box>
            }

          </List>
        </Container>
      </LocalizationProvider>
    </>
  );
}

export default Vouchers;
